// WooCommerce API Service for Duchess Collection
// This service handles all API calls to the headless WooCommerce WordPress site

import OAuth from 'oauth-1.0a';
import CryptoJS from 'crypto-js';

// Determine the correct base URL based on environment
const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8888/Website/duchessCollection';
    } else if (hostname.includes('staging')) {
      return 'https://staging.belvoircastle.com/api/duchess-proxy.php';
    } else {
      return 'https://www.belvoircastle.com/api/duchess-proxy.php';
    }
  }
  return 'https://www.belvoircastle.com/api/duchess-proxy.php';
};

const BASE_URL = getBaseURL();
const WOOCOMMERCE_API_BASE = BASE_URL.includes('duchess-proxy.php') ? BASE_URL : `${BASE_URL}/wp-json/wc/v3`;
const STORE_API_BASE = BASE_URL.includes('duchess-proxy.php') ? BASE_URL : `${BASE_URL}/wp-json/wc/store/v1`;

const CONSUMER_KEY = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY || '';
const CONSUMER_SECRET = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET || '';

// OAuth1 configuration for HTTP connections
const oauth = OAuth({
  consumer: {
    key: CONSUMER_KEY,
    secret: CONSUMER_SECRET
  },
  signature_method: 'HMAC-SHA1',
  hash_function(base_string, key) {
    return CryptoJS.HmacSHA1(base_string, key).toString(CryptoJS.enc.Base64);
  }
});

// Debug: Log the credentials being used

// Attempt to parse JSON; if the response is HTML or other content, provide a helpful error
const parseJsonOrReportHtml = async (response, contextLabel = 'Response') => {
  try {
    const contentType = response.headers?.get('content-type') || '';
    const bodyText = await response.text();

    // Fast path when header is JSON
    if (contentType.includes('application/json')) {
      return JSON.parse(bodyText);
    }

    // Try JSON anyway (some servers mislabel)
    try {
      return JSON.parse(bodyText);
    } catch (_) {
      const snippet = bodyText.slice(0, 500);

      throw new SyntaxError(`Non-JSON response (${response.status} ${response.statusText}). Content-Type: ${contentType}. This usually means the proxy returned HTML (e.g., 404/redirect).`);
    }
  } catch (e) {
    // Re-throw with original error
    throw e;
  }
};

// Helper function to create OAuth1 headers for HTTP connections
const createOAuthHeaders = (url, method = 'GET') => {
  const requestData = {
    url: url,
    method: method
  };
  
  return oauth.toHeader(oauth.authorize(requestData));
};

// Helper function to create basic auth header (for HTTPS connections)
const createAuthHeader = () => {
  const credentials = btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
  return `Basic ${credentials}`;
};

// Helper function to create query parameter authentication (legacy)
const createQueryAuth = () => {
  return `consumer_key=${CONSUMER_KEY}&consumer_secret=${CONSUMER_SECRET}`;
};

// Helper function to build query parameters
const buildQueryParams = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// =========================
// Store API Nonce management
// =========================
let storeApiNonce = null;
let storeApiNonceLastUpdatedMs = 0;

const updateNonceFromResponse = (response) => {
  try {
    const nonceHeader = response.headers?.get('Nonce');
    if (nonceHeader) {
      storeApiNonce = nonceHeader;
      storeApiNonceLastUpdatedMs = Date.now();
    }
  } catch (_) {
    // ignore
  }
};

const buildStoreHeaders = (includeNonce = false) => {
  const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  const envNonce = process.env.REACT_APP_WC_STORE_API_NONCE;
  if (envNonce) headers['X-WC-Store-API-Nonce'] = envNonce;
  if (includeNonce && storeApiNonce) headers['Nonce'] = storeApiNonce;
  return headers;
};

const fetchStoreApi = async (url, { method = 'GET', body = undefined, requireNonce = false } = {}) => {
  // Ensure we have a nonce when required
  if (requireNonce && !storeApiNonce) {
    try {

      const preResp = await fetch(`${STORE_API_BASE}/cart`, {
        method: 'GET',
        headers: buildStoreHeaders(false),
        credentials: 'include',
        mode: 'cors'
      });
      updateNonceFromResponse(preResp);

      // Consume the body to avoid stream locking warnings
      try { await preResp.json(); } catch (_) {}
    } catch (error) {

    }
  }

  const response = await fetch(url, {
    method,
    headers: buildStoreHeaders(requireNonce),
    credentials: 'include',
    mode: 'cors',
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  updateNonceFromResponse(response);

  // If nonce is missing/expired, refresh once and retry
  if (requireNonce && response.status === 401) {
    try {
      const refresh = await fetch(`${STORE_API_BASE}/cart`, {
        method: 'GET',
        headers: buildStoreHeaders(false),
        credentials: 'include',
        mode: 'cors'
      });
      updateNonceFromResponse(refresh);
      try { await refresh.json(); } catch (_) {}
    } catch (_) {}

    const retry = await fetch(url, {
      method,
      headers: buildStoreHeaders(true),
      credentials: 'include',
      mode: 'cors',
      body: body !== undefined ? JSON.stringify(body) : undefined
    });
    updateNonceFromResponse(retry);
    return retry;
  }

  return response;
};

export const woocommerceService = {
  // =========================
  // Store API (Blocks) helpers
  // These power on-site checkout with Stripe via WooCommerce
  // =========================

  // Build headers for Store API (optionally include nonce if provided)
  _getStoreApiHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const nonce = process.env.REACT_APP_WC_STORE_API_NONCE;
    if (nonce) headers['X-WC-Store-API-Nonce'] = nonce;
    return headers;
  },

  // Get current cart
  async getCart() {
    const url = `${STORE_API_BASE}/cart`;
    const response = await fetchStoreApi(url, { method: 'GET', requireNonce: false });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to get cart: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }
    return response.json();
  },

  // Add a single item to cart (Store API)
  // item: { id: productId, quantity, variation?: [{attribute, value}], variation_id?: number }
  async addCartItem(item) {
    const url = `${STORE_API_BASE}/cart/add-item`;
    const body = {
      id: item.id,
      quantity: item.quantity || 1,
      variation_id: item.variation_id,
      variation: item.variation
    };
    const response = await fetchStoreApi(url, { method: 'POST', body, requireNonce: true });
    if (!response.ok) {
      let errorMsg = 'Unknown error';
      try {
        const errJson = await response.json();
        errorMsg = errJson.message || (errJson.data && errJson.data.message) || errorMsg;
      } catch (_) {
        try { errorMsg = await response.text(); } catch (_) {}
      }
      throw new Error(`Failed to add cart item: ${response.status} ${response.statusText} - ${errorMsg}`);
    }
    return response.json();
  },

  // Remove all items from cart
  async clearCart() {
    const url = `${STORE_API_BASE}/cart/items`;
    const response = await fetchStoreApi(url, { method: 'DELETE', requireNonce: true });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to clear cart: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }
    return response.json();
  },

  // Replace cart with provided items (helper)
  async setCartItems(items = []) {
    await this.clearCart();
    for (const item of items) {
      await this.addCartItem(item);
    }
    const cart = await this.getCart();
    if (!cart || !cart.items_count) {
      throw new Error('Cart is empty after adding items. Verify product IDs and variations.');
    }
    return cart;
  },

  // Checkout using WooCommerce Store API with Stripe on-site
  // params: {
  //   billing_address, shipping_address, customer_note?,
  //   payment_method?: 'stripe' | 'woocommerce_payments',
  //   payment_data?: object (as required by the Stripe gateway),
  // }
  // Returns Store API checkout response (may include next_actions/client_secret)
  async checkoutWithStripe(params) {
    const {
      billing_address,
      shipping_address,
      customer_note = '',
      payment_method = 'stripe',
      payment_data = {}
    } = params || {};

    if (!billing_address) throw new Error('billing_address is required');

    const url = `${STORE_API_BASE}/checkout`;

    // Some gateways (e.g., WooCommerce Stripe Gateway) expect classic billing_* keys
    // inside payment_data even when using the Store API. Provide a compatibility layer.
    const paymentDataEnhanced = {
      ...payment_data,
      // Billing fallbacks
      billing_first_name: billing_address?.first_name,
      billing_last_name: billing_address?.last_name,
      billing_email: billing_address?.email,
      // Common aliases some gateways expect
      email: billing_address?.email,
      customer_email: billing_address?.email,
      payer_email: billing_address?.email,
      billing_phone: billing_address?.phone,
      billing_address_1: billing_address?.address_1,
      billing_address_2: billing_address?.address_2 || '',
      billing_city: billing_address?.city,
      billing_postcode: billing_address?.postcode,
      billing_country: billing_address?.country,
      // Shipping fallbacks (some gateways read these as well)
      shipping_first_name: (shipping_address || billing_address)?.first_name,
      shipping_last_name: (shipping_address || billing_address)?.last_name,
      shipping_address_1: (shipping_address || billing_address)?.address_1,
      shipping_address_2: (shipping_address || billing_address)?.address_2 || '',
      shipping_city: (shipping_address || billing_address)?.city,
      shipping_postcode: (shipping_address || billing_address)?.postcode,
      shipping_country: (shipping_address || billing_address)?.country,
    };

    // Some WooCommerce Store API versions/plugins validate payment_data as an array
    // of { key, value } pairs rather than an object. Convert if needed.
    const paymentDataAsArray = Object.entries(paymentDataEnhanced)
      .filter(([key, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => ({ key, value: String(value) }));

    const payload = {
      billing_address,
      shipping_address: shipping_address || billing_address,
      customer_note,
      payment_method,
      payment_data: paymentDataAsArray
    };


    try {
      const response = await fetchStoreApi(url, { method: 'POST', body: payload, requireNonce: true });

      if (!response.ok) {
        let msg = 'Unknown error';
        let errorDetails = null;
        let responseText = '';
        
        try {
          // Try to get the response body first
          responseText = await response.clone().text();

          // Try to parse as JSON
          const errJson = JSON.parse(responseText);
          errorDetails = errJson;
          
          // Extract error message from different possible locations
          msg = errJson.message || 
                errJson.data?.message || 
                errJson.error || 
                errJson.data?.error || 
                errJson.code || 
                msg;
          
          // Check for payment processing failures
          if (errJson.payment_result && errJson.payment_result.payment_status === 'failure') {
            const paymentDetails = errJson.payment_result.payment_details || [];
            const errorDetail = paymentDetails.find(detail => detail.key === 'errorMessage');
            if (errorDetail) {
              msg = `Payment failed: ${errorDetail.value}`;
            }
            
            // Add order information for debugging
            if (errJson.order_id) {
              msg += ` (Order #${errJson.order_id} created but payment failed)`;
            }
          }
          
          // Check for specific error types
          if (errJson.code === 'woocommerce_rest_checkout_missing_payment_method') {
            msg = 'Payment method not configured properly. Please check Stripe settings.';
          } else if (errJson.code === 'woocommerce_rest_checkout_invalid_payment_method') {
            msg = 'Invalid payment method. Please check Stripe configuration.';
          } else if (errJson.code === 'woocommerce_rest_no_route') {
            msg = 'Store API endpoint not found. Check WooCommerce version and blocks.';
          } else if (errJson.code === 'rest_cookie_invalid_nonce') {
            msg = 'Invalid security token. Please refresh the page and try again.';
          }
          
          // If we have validation errors, extract them
          if (errJson.data && errJson.data.details) {
            const details = Object.values(errJson.data.details).flat();
            if (details.length > 0) {
              msg = `Validation errors: ${details.join(', ')}`;
            }
          }
          
        } catch (parseError) {
          // If JSON parsing fails, use the raw text
          if (responseText) {
            msg = responseText.substring(0, 200); // Limit message length
          } else {
            msg = `HTTP ${response.status}: ${response.statusText}`;
          }
        }

        throw new Error(`Checkout failed: ${msg}`);
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  // Utility: try to extract a Stripe client_secret from the Store API response
  // Returns string | null
  extractStripeClientSecret(checkoutResponse) {
    if (!checkoutResponse) return null;
    // Common shapes seen in Stripe for Woo Blocks
    // 1) checkoutResponse.payment_result?.payment_details?.client_secret
    const cs1 = checkoutResponse?.payment_result?.payment_details?.client_secret;
    if (cs1) return cs1;
    // 2) checkoutResponse?.payment_result?.client_secret
    const cs2 = checkoutResponse?.payment_result?.client_secret;
    if (cs2) return cs2;
    // 3) checkoutResponse?.client_secret
    const cs3 = checkoutResponse?.client_secret;
    if (cs3) return cs3;
    return null;
  },

  // Get available payment methods from Store API
  async getPaymentMethods() {
    try {
      // Try different possible payment methods endpoints
      const endpoints = [
        `${STORE_API_BASE}/payment-methods`,
        `${STORE_API_BASE}/payments/methods`,
        `${WOOCOMMERCE_API_BASE}/payment_gateways`,
        `${BASE_URL}/wp-json/wc/v3/payment_gateways`
      ];
      
      let lastError = null;
      
      for (const url of endpoints) {
        try {

          if (url.includes('/wc/v3/')) {
            // Use OAuth for v3 API
            const oauthHeaders = createOAuthHeaders(url, 'GET');
            const response = await fetch(url, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...oauthHeaders
              },
            });
            
            if (response.ok) {
              const methods = await response.json();

              return methods;
            } else {

            }
          } else {
            // Use Store API
            const response = await fetchStoreApi(url, { method: 'GET', requireNonce: false });
            
            if (response.ok) {
              const methods = await response.json();

              return methods;
            } else {

            }
          }
        } catch (error) {

          lastError = error;
        }
      }
      
      throw new Error(`All payment methods endpoints failed. Last error: ${lastError?.message || 'Unknown error'}`);
      
    } catch (error) {

      throw error;
    }
  },

  // Debug function to check Store API status
  async debugStoreApi() {
    try {

      // Check cart
      try {
        const cart = await this.getCart();

      } catch (error) {

      }
      
      // Check payment methods
      try {
        const methods = await this.getPaymentMethods();

      } catch (error) {

      }
      
      // Check available endpoints

      const testEndpoints = [
        `${STORE_API_BASE}`,
        `${STORE_API_BASE}/payment-methods`,
        `${WOOCOMMERCE_API_BASE}`,
        `${WOOCOMMERCE_API_BASE}/payment_gateways`
      ];
      
      for (const endpoint of testEndpoints) {
        try {
          const response = await fetch(endpoint, { method: 'HEAD' });

        } catch (error) {

        }
      }
      
      // Check nonce status


      return {
        success: true,
        nonce: storeApiNonce,
        urls: {
          baseUrl: BASE_URL,
          storeApiBase: STORE_API_BASE,
          woocommerceApiBase: WOOCOMMERCE_API_BASE
        }
      };
    } catch (error) {

      return { success: false, error: error.message };
    }
  },

  // Fetch all products with pagination and filtering
  async getProducts(options = {}) {
    try {
      const {
        page = 1,
        per_page = 12,
        category = '',
        search = '',
        status = 'publish',
        featured = false,
        on_sale = false,
        orderby = 'date',
        order = 'desc'
      } = options;

      const params = {
        page,
        per_page,
        status,
        orderby,
        order
      };

      // Add optional filters
      if (category) params.category = category;
      if (search) params.search = search;
      if (featured) params.featured = true;
      if (on_sale) params.on_sale = true;

      const queryString = buildQueryParams(params);
      
      let url, headers;
      if (BASE_URL.includes('duchess-proxy.php')) {
        // Use proxy
        const proxyParams = new URLSearchParams();
        proxyParams.set('path', 'products');
        if (queryString) {
          // Add original query params to proxy URL
          const originalParams = new URLSearchParams(queryString);
          originalParams.forEach((value, key) => {
            proxyParams.set(key, value);
          });
        }
        url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
        headers = {}; // Proxy handles auth
      } else {
        // Direct API call
        url = `${WOOCOMMERCE_API_BASE}/products${queryString ? `?${queryString}` : ''}`;
        headers = createOAuthHeaders(url, 'GET');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
        mode: 'cors'
      });

      if (!response.ok) {
        // Attempt to parse JSON or report HTML response body
        let errorData = {};
        try {
          errorData = await parseJsonOrReportHtml(response.clone(), 'Products (error)');
        } catch (e) {
          // If non-JSON, include message only
          errorData = { message: e.message };
        }

        throw new Error(`Failed to fetch products: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
      }

      const products = await parseJsonOrReportHtml(response.clone(), 'Products');
      if (!Array.isArray(products)) {
        const message = typeof products?.message === 'string' ? products.message : 'Unexpected response shape (expected array)';

        throw new Error(message);
      }

      return products;
    } catch (error) {

      throw error;
    }
  },

  // Fetch a single product by ID
  async getProduct(productId) {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/products/${productId}`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...oauthHeaders
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status} ${response.statusText}`);
      }

      const product = await response.json();
      return product;
    } catch (error) {

      throw error;
    }
  },

  // Fetch product categories
  async getCategories(options = {}) {
    try {
      const {
        page = 1,
        per_page = 100,
        parent = 0,
        orderby = 'name',
        order = 'asc'
      } = options;

      const params = {
        page,
        per_page,
        parent,
        orderby,
        order
      };

      const queryString = buildQueryParams(params);
      
      let url, headers;
      if (BASE_URL.includes('duchess-proxy.php')) {
        // Use proxy
        const proxyParams = new URLSearchParams();
        proxyParams.set('path', 'products/categories');
        if (queryString) {
          // Add original query params to proxy URL
          const originalParams = new URLSearchParams(queryString);
          originalParams.forEach((value, key) => {
            proxyParams.set(key, value);
          });
        }
        url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
        headers = {}; // Proxy handles auth
      } else {
        // Direct API call
        url = `${WOOCOMMERCE_API_BASE}/products/categories${queryString ? `?${queryString}` : ''}`;
        headers = createOAuthHeaders(url, 'GET');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
        mode: 'cors'
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await parseJsonOrReportHtml(response.clone(), 'Categories (error)');
        } catch (e) {
          errorData = { message: e.message };
        }
        throw new Error(`Failed to fetch categories: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
      }

      const categories = await parseJsonOrReportHtml(response.clone(), 'Categories');
      if (!Array.isArray(categories)) {
        const message = typeof categories?.message === 'string' ? categories.message : 'Unexpected response shape (expected array)';

        throw new Error(message);
      }
      return categories;
    } catch (error) {

      throw error;
    }
  },

  // Search products
  async searchProducts(searchTerm, options = {}) {
    try {
      const searchOptions = {
        ...options,
        search: searchTerm
      };
      return await this.getProducts(searchOptions);
    } catch (error) {

      throw error;
    }
  },

  // Fetch featured products
  async getFeaturedProducts(options = {}) {
    try {
      const featuredOptions = {
        ...options,
        featured: true
      };
      return await this.getProducts(featuredOptions);
    } catch (error) {

      throw error;
    }
  },

  // Fetch products on sale
  async getSaleProducts(options = {}) {
    try {
      const saleOptions = {
        ...options,
        on_sale: true
      };
      return await this.getProducts(saleOptions);
    } catch (error) {

      throw error;
    }
  },

  // Fetch products by category
  async getProductsByCategory(categoryId, options = {}) {
    try {
      const categoryOptions = {
        ...options,
        category: categoryId
      };
      return await this.getProducts(categoryOptions);
    } catch (error) {

      throw error;
    }
  },

  // Get product variations (for variable products)
  async getProductVariations(productId) {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/products/${productId}/variations`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...oauthHeaders
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product variations: ${response.status} ${response.statusText}`);
      }

      const variations = await response.json();
      return variations;
    } catch (error) {

      throw error;
    }
  },

  // Get product reviews
  async getProductReviews(productId, options = {}) {
    try {
      const {
        page = 1,
        per_page = 10,
        status = 'approved'
      } = options;

      const params = {
        product: productId,
        page,
        per_page,
        status
      };

      const queryString = buildQueryParams(params);
      const url = `${WOOCOMMERCE_API_BASE}/products/reviews${queryString ? `?${queryString}` : ''}`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...oauthHeaders
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product reviews: ${response.status} ${response.statusText}`);
      }

      const reviews = await response.json();
      return reviews;
    } catch (error) {

      throw error;
    }
  },

  // Test API connection
  async testConnection() {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/system_status`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        throw new Error(`API connection failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {

      return {
        success: false,
        error: error.message
      };
    }
  },

  // Create order
  async createOrder(orderData) {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/orders`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'POST');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to create order: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
      }

      const order = await response.json();
      return order;
    } catch (error) {

      throw error;
    }
  },

  // Get order by ID
  async getOrder(orderId) {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/orders/${orderId}`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'GET');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch order: ${response.status} ${response.statusText}`);
      }

      const order = await response.json();
      return order;
    } catch (error) {

      throw error;
    }
  },

  // Update order status
  async updateOrderStatus(orderId, status) {
    try {
      const url = `${WOOCOMMERCE_API_BASE}/orders/${orderId}`;
      
      // Use OAuth1 for HTTP connections
      const oauthHeaders = createOAuthHeaders(url, 'PUT');

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...oauthHeaders
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update order status: ${response.status} ${response.statusText}`);
      }

      const order = await response.json();
      return order;
    } catch (error) {

      throw error;
    }
  }
};

export default woocommerceService;